import ReactMarkdown from 'react-markdown';
import { IChatMessage } from '@/types/ai-chat.types';

// Normalize bullet characters to proper markdown list syntax so ReactMarkdown
// renders them as <ul><li> elements (preserving line breaks between items).
function normalizeBullets(text: string): string {
  return text.replace(/^[•·]\s*/gm, '- ');
}
import { parseContentWithCharts } from '@/utils/content-parser';
import { SimpleChartEngine } from '@/features/360-analysis/charts/SimpleChartEngine/SimpleChartEngine';
import { cn } from '@/lib/utils';

interface IProps {
  message: IChatMessage;
  isStreaming?: boolean;
}

export const ChatMessage = ({ message, isStreaming }: IProps) => {
  const isUser = message.role === 'user';

  const chartMap = new Map(
    (message.chart_data ?? []).map((c) => [c.id, c]),
  );

  const parts = isUser ? null : parseContentWithCharts(message.content);

  // Charts from chart_data that are NOT embedded inline in the response text
  const inlineChartIds = new Set(
    (parts ?? []).filter((p) => p.type === 'chart').map((p) => p.chartId!),
  );
  const appendedCharts = (message.chart_data ?? []).filter((c) => !c.id || !inlineChartIds.has(c.id));

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[90%] rounded-2xl px-4 py-3 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-card border rounded-bl-sm',
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {parts?.map((part, i) => {
              if (part.type === 'chart') {
                const chart = chartMap.get(part.chartId!);
                if (!chart) return null;
                return (
                  <div key={i} className="w-full min-w-[280px]">
                    <SimpleChartEngine {...chart} compact />
                  </div>
                );
              }
              return (
                <ReactMarkdown
                  key={i}
                  components={{
                    p: ({ children }) => (
                      <p className="leading-relaxed mb-1 last:mb-0">{children}</p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc pl-4 space-y-0.5 my-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-4 space-y-0.5 my-1">{children}</ol>
                    ),
                    li: ({ children }) => <li>{children}</li>,
                    code: ({ children }) => (
                      <code className="bg-muted rounded px-1 py-0.5 text-xs font-mono">{children}</code>
                    ),
                    h1: ({ children }) => <h1 className="font-bold text-base mt-2 mb-1">{children}</h1>,
                    h2: ({ children }) => <h2 className="font-bold text-sm mt-2 mb-1">{children}</h2>,
                    h3: ({ children }) => <h3 className="font-semibold text-sm mt-1 mb-0.5">{children}</h3>,
                  }}
                >
                  {normalizeBullets(part.content)}
                </ReactMarkdown>
              );
            })}

            {/* Charts not embedded in response text — shown below */}
            {appendedCharts.map((chart) => (
              <div key={chart.id} className="w-full min-w-[280px] mt-1">
                <SimpleChartEngine {...chart} compact />
              </div>
            ))}

            {/* Inline blinking cursor during streaming */}
            {isStreaming && (
              <span className="inline-block w-0.5 h-4 bg-foreground/70 animate-[blink_1s_step-end_infinite] align-text-bottom" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
